import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, Building2, MapPin, Plus, X } from 'lucide-react';
import { useCustomers, Customer } from '@/hooks/useCustomers';
import { cn } from '@/lib/utils';

interface CustomerSelectProps {
  value: string | null;
  onChange: (customerId: string, customerName: string) => void;
  required?: boolean;
  placeholder?: string;
  className?: string;
  error?: string;
}

export function CustomerSelect({
  value,
  onChange,
  required = false,
  placeholder = 'Select a customer...',
  className,
  error,
}: CustomerSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: customersData, isLoading } = useCustomers({
    search: searchQuery || undefined,
    status: 'active',
    limit: 50,
  });

  const customers = customersData?.customers || [];

  // Find selected customer
  const selectedCustomer = useMemo(() => {
    if (!value) return null;
    return customers.find((c) => c.id === value) || null;
  }, [value, customers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (customer: Customer) => {
    onChange(customer.id, customer.name);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
          error
            ? 'border-red-300 bg-red-50'
            : 'border-gray-300 bg-white hover:border-gray-400',
          isOpen && 'border-primary-500 ring-2 ring-primary-500 ring-offset-1'
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
          {selectedCustomer ? (
            <div className="min-w-0">
              <span className="font-medium text-xtravirt-dark truncate block">
                {selectedCustomer.name}
              </span>
              {(selectedCustomer.industry || selectedCustomer.region) && (
                <span className="text-xs text-gray-500 truncate block">
                  {[selectedCustomer.industry, selectedCustomer.region].filter(Boolean).join(' • ')}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {selectedCustomer && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded p-1 hover:bg-gray-100"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-400 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </button>

      {/* Error message */}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg">
          {/* Search input */}
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search customers..."
                className="w-full rounded-md border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Customer list */}
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                Loading customers...
              </div>
            ) : customers.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-gray-500">
                  {searchQuery ? 'No customers found' : 'No active customers'}
                </p>
                <Link
                  to="/customers?create=true"
                  className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  <Plus className="h-4 w-4" />
                  Create a new customer
                </Link>
              </div>
            ) : (
              <ul className="py-1">
                {customers.map((customer) => (
                  <li key={customer.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(customer)}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-2 text-left hover:bg-gray-50',
                        customer.id === value && 'bg-primary-50'
                      )}
                    >
                      <Building2 className="h-4 w-4 flex-shrink-0 text-gray-400 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-xtravirt-dark truncate">
                          {customer.name}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {customer.industry && <span>{customer.industry}</span>}
                          {customer.region && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {customer.region}
                            </span>
                          )}
                        </div>
                      </div>
                      {customer.adoptionScore > 0 && (
                        <span className="flex-shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                          {customer.adoptionScore}%
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-100 p-2">
            <Link
              to="/customers?create=true"
              className="flex items-center justify-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              <Plus className="h-4 w-4" />
              Create new customer
            </Link>
          </div>
        </div>
      )}

      {/* Required indicator */}
      {required && !selectedCustomer && (
        <input
          type="text"
          required
          value=""
          onChange={() => {}}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default CustomerSelect;
